import { CheckCircle2, Briefcase, Workflow, Users } from "lucide-react";
import { Section, Overline } from "@/components/site/Section";
import CtaBanner from "@/components/site/CtaBanner";

const PLACEMENT_IMG = "https://images.unsplash.com/photo-1542744173-8e7e53415bb0?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODl8MHwxfHNlYXJjaHwxfHxwcm9mZXNzaW9uYWwlMjBjb3Jwb3JhdGUlMjB3b3JraW5nfGVufDB8fHx8MTc3ODc3NjYwNXww&ixlib=rb-4.1.0&q=85";
const RPO_IMG = "https://images.unsplash.com/photo-1542744095-fcf48d80b0fd?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODl8MHwxfHNlYXJjaHwzfHxwcm9mZXNzaW9uYWwlMjBjb3Jwb3JhdGUlMjB3b3JraW5nfGVufDB8fHx8MTc3ODc3NjYwNXww&ixlib=rb-4.1.0&q=85";

const placementBenefits = [
  "Faster time-to-fill",
  "Access to passive candidates",
  "Industry-specialized recruiting",
  "Reduced hiring risk",
  "Quality-focused candidate screening",
];
const placementRoles = [
  "Software Engineers", "AI & ML Engineers", "Electrical Engineers", "Mechanical Engineers",
  "Aerospace Engineers", "Project Managers", "HR Professionals", "Financial Analysts",
  "Controllers & Accountants", "Healthcare Professionals", "Legal Assistants & Attorneys",
  "Operations Managers", "Sales Executives", "Supply Chain Professionals",
];

const rpoIncludes = [
  "Full-cycle recruiting", "Dedicated recruiters", "Candidate sourcing",
  "Recruitment coordination", "Interview scheduling", "Talent pipeline development",
  "Recruitment analytics", "Employer branding support",
];
const rpoBenefits = [
  "Flexible & scalable recruiting", "Lower recruitment costs", "Faster hiring delivery",
  "Experienced recruiters", "Improved candidate experience", "Transparent reporting",
];

const hrIncludes = [
  "HR Operations Support", "Employee Onboarding", "HR Compliance Support",
  "Performance Management Assistance", "Employee Engagement Support",
  "HR Policy Development", "Workforce Planning",
];

const services = [
  { id: "direct-placement", num: "01", icon: Briefcase, label: "Direct Placement" },
  { id: "rpo", num: "02", icon: Workflow, label: "RPO Solutions" },
  { id: "hr-services", num: "03", icon: Users, label: "HR Services" },
];

function CheckList({ items, accent = false }) {
  return (
    <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3">
      {items.map((b) => (
        <li key={b} className="flex items-start gap-2 text-sm">
          <CheckCircle2 className={`h-4 w-4 mt-0.5 ${accent ? "text-[hsl(var(--accent))]" : "text-[hsl(var(--primary))]"}`} /> {b}
        </li>
      ))}
    </ul>
  );
}

function ServicesHero() {
  return (
    <Section className="pb-12">
      <Overline className="mb-5">Our Services</Overline>
      <h1 className="font-display text-5xl md:text-6xl tracking-tight leading-[1.05] max-w-4xl">
        Recruitment & HR solutions, <span className="italic text-[hsl(var(--primary))]">tailored to your business.</span>
      </h1>
      <p className="mt-6 max-w-3xl text-lg md:text-xl text-muted-foreground">
        Three integrated practice areas — Direct Placement, Recruitment Process Outsourcing,
        and HR Services. Mix and match to meet your workforce goals.
      </p>

      <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-px bg-border border border-border rounded-xl overflow-hidden">
        {services.map((s) => (
          <a
            key={s.id}
            href={`#${s.id}`}
            data-testid={`service-nav-${s.id}`}
            className="bg-[hsl(var(--background))] p-7 hover:bg-[hsl(var(--primary))] hover:text-white transition-colors group"
          >
            <div className="flex items-center justify-between">
              <p className="overline group-hover:!text-white/80">{s.num}</p>
              <s.icon className="h-5 w-5 text-[hsl(var(--accent))] group-hover:text-white" />
            </div>
            <h3 className="mt-4 font-display text-2xl">{s.label}</h3>
            <p className="mt-2 text-sm text-muted-foreground group-hover:text-white/80">Jump to section ↓</p>
          </a>
        ))}
      </div>
    </Section>
  );
}

function DirectPlacementSection() {
  return (
    <Section id="direct-placement" className="bg-[hsl(var(--secondary))]/40 border-y border-border scroll-mt-24">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
        <div className="lg:col-span-5">
          <Overline className="mb-3">01 — Direct Placement</Overline>
          <h2 className="font-display text-4xl md:text-5xl tracking-tight leading-[1.05]">
            Permanent hires across critical functions.
          </h2>
          <p className="mt-5 text-muted-foreground text-lg leading-relaxed">
            X-HRverse helps organizations identify and hire highly qualified professionals for
            permanent positions across technical, professional, and leadership functions.
          </p>
          <div className="mt-7 aspect-[4/3] overflow-hidden rounded-xl border border-border">
            <img src={PLACEMENT_IMG} alt="Direct placement" className="w-full h-full object-cover" />
          </div>
        </div>
        <div className="lg:col-span-7 space-y-8">
          <div className="bg-[hsl(var(--background))] border border-border rounded-xl p-7 md:p-8">
            <h3 className="font-display text-xl mb-4">Key Benefits</h3>
            <CheckList items={placementBenefits} />
          </div>
          <div className="bg-[hsl(var(--background))] border border-border rounded-xl p-7 md:p-8">
            <h3 className="font-display text-xl mb-4">Roles We Place</h3>
            <div className="flex flex-wrap gap-2">
              {placementRoles.map((r) => (
                <span key={r} className="px-3 py-1.5 rounded-md bg-[hsl(var(--secondary))] text-foreground/80 text-xs border border-border">
                  {r}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>
    </Section>
  );
}

function RpoSection() {
  return (
    <Section id="rpo" className="scroll-mt-24">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
        <div className="lg:col-span-5 lg:order-2">
          <Overline className="mb-3">02 — RPO Solutions</Overline>
          <h2 className="font-display text-4xl md:text-5xl tracking-tight leading-[1.05]">
            Recruitment Process Outsourcing that flexes with you.
          </h2>
          <p className="mt-5 text-muted-foreground text-lg leading-relaxed">
            Scalable recruiting support designed to optimize hiring operations, reduce costs,
            and improve hiring outcomes — from one project to your full talent function.
          </p>
          <div className="mt-7 aspect-[4/3] overflow-hidden rounded-xl border border-border">
            <img src={RPO_IMG} alt="RPO solutions" className="w-full h-full object-cover" />
          </div>
        </div>
        <div className="lg:col-span-7 lg:order-1 space-y-8">
          <div className="bg-[hsl(var(--secondary))]/40 border border-border rounded-xl p-7 md:p-8">
            <h3 className="font-display text-xl mb-4">What's Included</h3>
            <CheckList items={rpoIncludes} />
          </div>
          <div className="bg-[hsl(var(--secondary))]/40 border border-border rounded-xl p-7 md:p-8">
            <h3 className="font-display text-xl mb-4">Why Businesses Choose Our RPO</h3>
            <CheckList items={rpoBenefits} accent />
          </div>
        </div>
      </div>
    </Section>
  );
}

function HrServicesSection() {
  return (
    <Section id="hr-services" className="bg-[hsl(var(--secondary))]/40 border-y border-border scroll-mt-24">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
        <div className="lg:col-span-5">
          <Overline className="mb-3">03 — HR Services</Overline>
          <h2 className="font-display text-4xl md:text-5xl tracking-tight leading-[1.05]">
            Modern HR operations that scale with your team.
          </h2>
          <p className="mt-5 text-muted-foreground text-lg leading-relaxed">
            We support organizations with HR solutions that improve employee operations,
            compliance, and workforce efficiency — from onboarding to long-term planning.
          </p>
        </div>
        <div className="lg:col-span-7">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-px bg-border border border-border rounded-xl overflow-hidden">
            {hrIncludes.map((b, i) => (
              <div key={b} className="bg-[hsl(var(--background))] p-6">
                <p className="overline">{String(i + 1).padStart(2, "0")}</p>
                <h3 className="mt-2 font-display text-lg">{b}</h3>
              </div>
            ))}
          </div>
        </div>
      </div>
    </Section>
  );
}

export default function Services() {
  return (
    <div data-testid="page-services">
      <ServicesHero />
      <DirectPlacementSection />
      <RpoSection />
      <HrServicesSection />
      <CtaBanner
        title="Ready to design the right hiring engagement?"
        ctaLabel="Request Recruiting Support"
        testid="services-cta"
      />
    </div>
  );
}
